import type { ApiMediaKind, AssetFields, MediaAssetFields } from "../types";

/**
 * The asset shape: the fields Media and SpatialDataset responses share (the
 * original upload, what to show and how, and where processing is). They are
 * read here and nowhere else, so every screen agrees on what an asset is.
 */

const ASSET_FIELDS: ReadonlyArray<keyof AssetFields | "media_kind"> = [
  "original_file_key",
  "original_file",
  "display_url",
  "display_type",
  "thumbnail",
  "processing_status",
  "processing_error",
  "processing_details",
  "upload_details",
  "media_kind"
];

/**
 * The asset fields of an API media item or spatial dataset, copied unchanged.
 * Every place that builds a media object from a response spreads this in, so the
 * shape reaches components intact. Fields the response lacks stay absent.
 */
export const toAsset = (raw: unknown): MediaAssetFields => {
  if (!raw || typeof raw !== "object") return {};
  const source = raw as Record<string, unknown>;
  const asset: Record<string, unknown> = {};
  for (const key of ASSET_FIELDS) {
    if (source[key] !== undefined) asset[key] = source[key];
  }
  return asset as MediaAssetFields;
};

type ApiMedia = MediaAssetFields & { media_kind: ApiMediaKind };

/**
 * Media from our own API: it carries `media_kind`. External-data media doesn't,
 * and neither does media from a backend without the asset shape; both keep their
 * own handling.
 */
export const isApiMedia = (item: unknown): item is ApiMedia => Boolean((item as MediaAssetFields | null | undefined)?.media_kind);

type Displayable = { display_url?: string | null; url?: string | null };

/** What to show for API media: its rendition when there is one, else the original file. */
export const assetDisplayUrl = (item: Displayable | null | undefined): string => item?.display_url || item?.url || "";

/** An embed (YouTube, Sketchfab…): `display_url` is the iframe `src`. */
export const isEmbedAsset = (item: unknown): boolean => {
  const asset = item as MediaAssetFields | null | undefined;
  return asset?.display_type === "embed" || asset?.media_kind === "embed";
};

/**
 * The full-resolution image a tile falls back to while a fresh upload's thumbnail
 * isn't written yet. For API media only an image's original: a zip's or video's
 * original can't be shown as an <img>. Anything else keeps its primary image or URL.
 */
export const fullImageFallbackUrl = (item: any): string | undefined => {
  if (isApiMedia(item)) return item.media_kind === "image" ? (item as { url?: string | null }).url || undefined : undefined;
  return item?.primary_image?.url || item?.url || undefined;
};

// ── Media that isn't from our API ────────────────────────────────────────────
// External-data media, and items still being prepared in the upload editor (a
// local blob in `previewUrl`, a pasted embed's `embed_url`), carry their own
// fields. Media from our API is read through the asset fields only.

/** The URL a media item is shown from: `display_url`, else the original file. */
export const mediaSourceUrl = (item: any): string => {
  if (!item) return "";
  if (isApiMedia(item)) return assetDisplayUrl(item);
  return item.url || item.previewUrl || "";
};

/** An embed's iframe src, or "" when the item isn't an embed. */
export const embedSrc = (item: any): string => {
  if (!item) return "";
  if (isApiMedia(item)) return isEmbedAsset(item) ? item.display_url || "" : "";
  return item.embed_url || "";
};

/** A media item's thumbnail, or "" when it has none. */
export const mediaThumbnail = (item: any): string => item?.thumbnail || "";

/**
 * A media item with nothing to show, ever: an upload that never finished, or
 * failed. Callers show an "unavailable" state (and let the user delete it)
 * instead of a spinner that never clears.
 * - API media: until it is completed, or when it has neither a rendition nor an
 *   original (a completed download-only file still has its original).
 * - Other media: no URL, thumbnail, embed or file key on it.
 */
export const isMediaUnavailable = (item: any): boolean => {
  if (!item) return false;
  if (isApiMedia(item)) return item.processing_status !== "completed" || !(item.display_url || (item as { url?: string | null }).url);
  // Tabular media renders from its file key rather than a URL, so a key is a source too.
  return !(item.url || item.thumbnail || item.previewUrl || item.embed_url || item.original_file_key || item.file_key);
};
